import type { CapabilityCuration } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  clearCapabilityCuration,
  fetchAgentReports,
  fetchCurationQueue,
  fetchFeedbackSummary,
  fetchOwnerAnalytics,
  type MessageFeedbackInput,
  type ReportAgentInput,
  reportAgent,
  type ShareQuestionInput,
  saveCapabilityCuration,
  sendMessageFeedback,
  shareQuestion,
  triageReport
} from '@/api/engagement.ts'
import { queryKeys } from '@/hooks/use-api.ts'
import { makerKeys } from '@/hooks/use-maker.ts'

/**
 * Query hooks for the engagement surfaces.
 *
 * The two owner reads are gated on an `enabled` flag rather than fetched with the row:
 * "Your agents" lists every agent a maker owns, and eagerly loading analytics for all of
 * them would fire a fan of aggregate queries to render a page where most panels stay shut.
 *
 * Feedback is the one write here that does NOT invalidate its own read. The owner summary is
 * aggregated and delayed by design (AGM-121) — refetching it after a thumb would neither
 * change what it shows nor be visible to the person who clicked, and the message list
 * refetch below is what puts the thumb in its pressed state.
 */

export const engagementKeys = {
  analytics: (agentId: string) => ['maker', 'analytics', agentId] as const,
  feedbackSummary: (agentId: string) => ['maker', 'feedback', agentId] as const,
  reports: ['admin', 'marketplace', 'reports'] as const,
  curationQueue: ['admin', 'mcp', 'curation-queue'] as const
}

// ---------------------------------------------------------------------------
// Consumer plane
// ---------------------------------------------------------------------------

export const useSendMessageFeedback = (conversationId: string | undefined) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, ...input }: MessageFeedbackInput & { messageId: string }) =>
      sendMessageFeedback(messageId, input),
    onSuccess: () => {
      // `Message.feedback` is persisted, so the pressed state survives a reload only if the
      // message list is re-read. The component holds its own optimistic state for the
      // in-between, because this refetch is a round trip and the thumb must respond now.
      if (conversationId) void client.invalidateQueries({ queryKey: queryKeys.messages(conversationId) })
    }
  })
}

export const useReportAgent = (agentId: string) =>
  useMutation({ mutationFn: (input: ReportAgentInput) => reportAgent(agentId, input) })

export const useShareQuestion = (agentId: string) =>
  useMutation({ mutationFn: (input: ShareQuestionInput) => shareQuestion(agentId, input) })

// ---------------------------------------------------------------------------
// Owner plane
// ---------------------------------------------------------------------------

export const useOwnerAnalytics = (agentId: string, enabled: boolean) =>
  useQuery({
    queryKey: engagementKeys.analytics(agentId),
    queryFn: () => fetchOwnerAnalytics(agentId),
    enabled,
    staleTime: 60_000,
    // Aggregates over a window that moves in hours: three retries against a route that is
    // not on this deployment yet only delays the panel's own "not available" state.
    retry: false
  })

export const useFeedbackSummary = (agentId: string, enabled: boolean) =>
  useQuery({
    queryKey: engagementKeys.feedbackSummary(agentId),
    queryFn: () => fetchFeedbackSummary(agentId),
    enabled,
    staleTime: 60_000,
    retry: false
  })

// ---------------------------------------------------------------------------
// Administrator plane
// ---------------------------------------------------------------------------

export const useAgentReports = (enabled: boolean) =>
  useQuery({ queryKey: engagementKeys.reports, queryFn: fetchAgentReports, enabled, staleTime: 30_000, retry: false })

export const useTriageReport = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, status }: { reportId: string; status: 'triaged' | 'dismissed' }) =>
      triageReport(reportId, status),
    onSuccess: () => client.invalidateQueries({ queryKey: engagementKeys.reports })
  })
}

export const useCurationQueue = (enabled: boolean) =>
  useQuery({
    queryKey: engagementKeys.curationQueue,
    queryFn: fetchCurationQueue,
    enabled,
    staleTime: 30_000,
    retry: false
  })

export const useSaveCapabilityCuration = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ capabilityId, curation }: { capabilityId: string; curation: CapabilityCuration }) =>
      saveCapabilityCuration(capabilityId, curation),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: engagementKeys.curationQueue })
      // Curating a capability is what makes it visible to makers, so the builder's catalogue
      // read is stale the moment this succeeds.
      void client.invalidateQueries({ queryKey: makerKeys.capabilities })
    }
  })
}

export const useClearCapabilityCuration = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (capabilityId: string) => clearCapabilityCuration(capabilityId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: engagementKeys.curationQueue })
      void client.invalidateQueries({ queryKey: makerKeys.capabilities })
    }
  })
}
